'use client';

import {
    Dispatch,
    SetStateAction,
    createContext,
    useContext,
    useEffect,
    useRef,
    useState,
    useLayoutEffect,
} from 'react';
import { ReactLenis, useLenis } from '@studio-freight/react-lenis';

import MotionPathPlugin from 'gsap/MotionPathPlugin';
import React from 'react';
import ScrollTrigger from 'gsap/ScrollTrigger';
import gsap from 'gsap';

interface ContextProps {
    i18n?: { [x: string]: Record<string, string> | string } | any;
    lang?: string;
    translatedUri?: { [x: string]: Record<string, string> } | null;
    setTranslatedUri?:
        | Dispatch<
              SetStateAction<{
                  [x: string]: Record<string, string> | undefined | null;
              } | null>
          >
        | any;
    pageTitle?: string;
    setPageTitle?:
        | Dispatch<
              SetStateAction<{
                  [x: string]: Record<string, string> | undefined | null;
              } | null>
          >
        | any;
    menus?: {
        [x: string]: Record<string, unknown | Record<string, unknown>[]>[];
    };
    themeOptions?: {
        socials?: Record<string, string>[];
        helloBar?: {
            [lang: string]: {
                helloBarActive: string;
                helloBarText: string;
                helloBarCTALabel: string;
                helloBarCTAUrl: string;
            };
        };
    };
    dict?: Record<string, Record<string, string> | any>;
    lenis?: Record<string, Record<string, string> | any>;
    languages?: Array<{ code: string; name: string }>;
}

const GlobalContext = createContext<ContextProps>({
    i18n: {},
    lang: '',
    translatedUri: {},
    setTranslatedUri: undefined,
    pageTitle: '',
    setPageTitle: undefined,
    menus: {},
    themeOptions: {},
    dict: {},
    lenis: {},
    languages: [],
});

export const GlobalContextProvider = ({
    children,
    value,
}: {
    children: React.ReactNode;
    value: ContextProps;
}) => {
    gsap.registerPlugin(ScrollTrigger);
    gsap.registerPlugin(MotionPathPlugin);

    useLayoutEffect(() => {
        if (typeof window !== 'undefined') {
            history.scrollRestoration = 'manual';
        }
    }, []);

    const lang = value.lang || '';
    const i18n = value.i18n || {};
    const menus = value.menus || {};
    const themeOptions = value.themeOptions || {};
    const dict = value.dict || {};
    const languages = value.languages || [];

    return (
        <ReactLenis
            root
            options={{
                duration: 1.8,
                easing: (t: number) =>
                    Math.min(1, 1.001 - Math.pow(2, -10 * t)),
            }}>
            <GlobalContextInner value={{ lang, i18n, menus, themeOptions, dict, languages }}>
                {children}
            </GlobalContextInner>
        </ReactLenis>
    );
};

const GlobalContextInner = ({ value, children }: { value: any; children: React.ReactNode }) => {
    const { lang, i18n, menus, themeOptions, dict, languages } = value;
    const [translatedUri, setTranslatedUri] = useState<any>(undefined);
    const [pageTitle, setPageTitle] = useState<string | undefined>();

    const lenis = useLenis(() => {
        // called every scroll
    });

    return (
        <GlobalContext.Provider
            value={{
                i18n,
                lang,
                translatedUri,
                setTranslatedUri,
                pageTitle,
                setPageTitle,
                menus,
                themeOptions,
                dict,
                lenis,
                languages,
            }}>
            {children}
        </GlobalContext.Provider>
    );
};

export const useGlobalContext = () => useContext(GlobalContext);
